import * as React from 'react';
import { cx } from '../utils';
import { tintVar, type TintName } from '../types';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

export type SpinnerSize = 'sm' | 'md' | 'lg';
/* No `neutral` — an untinted spinner just omits the prop and
   inherits currentColor (via stroke="currentColor" on the SVG). */
export type SpinnerTint = TintName;

/* Bounded rest-prop allow-list (codex Card r1 lesson). */
type AllowedRest = Pick<React.HTMLAttributes<HTMLElement>, 'id' | 'title' | 'lang' | 'dir'> & {
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | boolean | number | undefined;
};
const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);
function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

export interface SpinnerProps extends StyledProps, UnsafeStyleProps, AllowedRest {
  size?: SpinnerSize;
  /* Semantic tint — drives stroke color via tintVar. Omit for the
     parent's currentColor. */
  tint?: SpinnerTint;
  /* Accessible label for AT users. Default `'Loading'`. Pass a
     more specific value when the surrounding context isn't obvious
     (e.g., `'Loading transactions'`). */
  ariaLabel?: string;
  /* When true, the spinner is presentational (decorative inside a
     labeled control like a Button) — emits aria-hidden=true + no
     role. Use this when the parent provides the loading semantics. */
  decorative?: boolean;
}

const SIZE_PX: Record<SpinnerSize, number> = {
  sm: 16,
  md: 24,
  lg: 32,
};

/**
 * Indeterminate loading indicator. CSS-animated circular spinner —
 * no JS animation loop, no rAF, no setInterval. The motion is a
 * pure CSS @keyframes rotation; respects `prefers-reduced-motion`
 * via the kit's global motion-token cascade (motion duration falls
 * back to button-press timing under reduced-motion).
 *
 * a11y modes:
 * - default: `role="status"` + `aria-label` + `aria-live="polite"`
 *   — AT announces the label on mount + when the label changes
 * - decorative: `aria-hidden="true"` + no role — for spinners
 *   embedded inside a labeled control (Button[loading], etc.)
 *   where the parent provides the semantics
 */
export const Spinner = React.forwardRef<SVGSVGElement, SpinnerProps>(function Spinner(
  { size = 'md', tint, ariaLabel = 'Loading', decorative, className, unsafe_style, ...rest },
  ref,
) {
  const restProps = pickAllowed(
    rest as Record<string, unknown>,
  ) as React.SVGAttributes<SVGSVGElement>;

  const px = SIZE_PX[size];
  const color = tintVar(tint);

  /* Respect an adopter-supplied aria-label via the rest pass-through
     — `<Spinner aria-label="Saving" />` should announce "Saving", not
     the default "Loading". Codex Spinner r1. */
  const restAriaLabel = (restProps as { 'aria-label'?: string })['aria-label'];

  const ariaProps = decorative
    ? ({ 'aria-hidden': true as const } as const)
    : ({
        role: 'status' as const,
        'aria-label': restAriaLabel ?? ariaLabel,
        'aria-live': 'polite' as const,
      } as const);

  return (
    <svg
      ref={ref}
      {...restProps}
      {...ariaProps}
      className={cx('j-spinner', `j-spinner-${size}`, className)}
      style={{ ...(color ? { color } : null), ...unsafe_style }}
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Track ring (24% opacity) */}
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" opacity="0.24" />
      {/* Animated arc — the dasharray creates a partial ring; the
          CSS animation rotates the whole SVG. */}
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="20 60"
      />
    </svg>
  );
});
